import type { ShapeKind } from '../constants/shapes'
import type { Tool, ToolContext, ToolPoint } from './Tool'

/**
 * Multi-click shape placer. Rectangle / ellipse take 2 clicks
 * (opposite corners of the bounding box); triangle takes 3 (each vertex).
 * Between clicks we stamp a small dot so a toddler can see progress.
 * On the final click we restore a baseline snapshot (wiping the dots)
 * and draw the final shape, then call onCommit to push a history entry.
 */
export class ShapeTool implements Tool {
  readonly id = 'shape'
  readonly manualSnapshot = true

  private kind: ShapeKind
  private vertices: ToolPoint[] = []
  private baseline: ImageData | null = null
  private onCommit: () => void

  constructor(kind: ShapeKind, onCommit: () => void) {
    this.kind = kind
    this.onCommit = onCommit
  }

  setKind(kind: ShapeKind): void {
    this.kind = kind
  }

  private required(): number {
    return this.kind === 'triangle' ? 3 : 2
  }

  onPointerDown(ctx: ToolContext, point: ToolPoint): void {
    const { canvas, ctx: c, color, brushSize } = ctx

    if (this.vertices.length === 0) {
      this.baseline = c.getImageData(0, 0, canvas.width, canvas.height)
    }
    this.vertices.push(point)

    if (this.vertices.length >= this.required()) {
      if (this.baseline) c.putImageData(this.baseline, 0, 0)
      this.drawShape(ctx, this.vertices)
      this.reset()
      this.onCommit()
      return
    }

    c.fillStyle = color.hex
    c.beginPath()
    c.arc(point.x, point.y, Math.max(brushSize.px / 2, 8), 0, Math.PI * 2)
    c.fill()
  }

  onPointerMove(): void {
    /* clicks, not drags — no preview */
  }

  onPointerUp(): void {
    /* no-op */
  }

  cancel(ctx: ToolContext): void {
    if (this.baseline) {
      try {
        ctx.ctx.putImageData(this.baseline, 0, 0)
      } catch {
        /* canvas may be unmounted */
      }
    }
    this.reset()
  }

  private reset(): void {
    this.vertices = []
    this.baseline = null
  }

  private drawShape(ctx: ToolContext, pts: ToolPoint[]): void {
    const { ctx: c, color, brushSize } = ctx
    c.lineJoin = 'round'
    c.lineCap = 'round'
    c.lineWidth = brushSize.px
    c.strokeStyle = color.hex

    if (this.kind === 'rectangle') {
      const [a, b] = pts
      const x = Math.min(a.x, b.x)
      const y = Math.min(a.y, b.y)
      const w = Math.abs(b.x - a.x)
      const h = Math.abs(b.y - a.y)
      c.strokeRect(x, y, w, h)
      return
    }

    if (this.kind === 'ellipse') {
      const [a, b] = pts
      const cx = (a.x + b.x) / 2
      const cy = (a.y + b.y) / 2
      const rx = Math.abs(b.x - a.x) / 2
      const ry = Math.abs(b.y - a.y) / 2
      c.beginPath()
      c.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2)
      c.stroke()
      return
    }

    if (this.kind === 'triangle') {
      const [a, b, d] = pts
      c.beginPath()
      c.moveTo(a.x, a.y)
      c.lineTo(b.x, b.y)
      c.lineTo(d.x, d.y)
      c.closePath()
      c.stroke()
    }
  }
}
